import { UserUpdateForm } from '@/pages/admin/users-table'
import { UserForm } from '@/pages/customer/profile'
import axios from '@/services/axios'

const fetchAllUsers = async (): Promise<User[]> => {
	const response = await axios.get('/user/get-all-users')
	return response.data.data
}

const fetchUsersData = async (
	data: FilterPayload,
): Promise<{ data: User[]; pagination: Pagination }> => {
	const queryParams = new URLSearchParams()
	queryParams.append('page', data.pageNumber)
	queryParams.append('limit', data.pageSize)

	if (data.filterColumn && data.filterValue) {
		queryParams.append('filterColumn', data.filterColumn)
		queryParams.append('filterValue', data.filterValue)
	}

	const queryString = queryParams.toString()

	const response = await axios.get(`/user/get-users?${queryString}`)
	return response.data
}

const fetchUserEarnings = async (): Promise<UserEarnings> => {
	const response = await axios.get('/booking/get-user-bookings-earnings')
	return response.data?.response?.data
}

const fetchUserBookings = async (
	data: Pick<FilterPayload, 'pageNumber' | 'pageSize'>,
): Promise<UserBooking[]> => {
	const queryParams = new URLSearchParams()

	queryParams.append('page', data.pageNumber)
	queryParams.append('limit', data.pageSize)

	const queryString = queryParams.toString()

	const response = await axios.get(`/booking/get-user-bookings?${queryString}`)
	return response.data?.response?.data
}

const fetchUserActiveBookings = async (): Promise<Array<UserBooking & { stand: Stand }>> => {
	const response = await axios.get(`/booking/get-user-active-bookings`)
	return response.data?.response?.data
}

const fetchUserCurrentBookings = async (
	data: Pick<FilterPayload, 'pageNumber' | 'pageSize'>,
): Promise<UserBooking[]> => {
	const queryParams = new URLSearchParams()

	queryParams.append('page', data.pageNumber)
	queryParams.append('limit', data.pageSize)

	const queryString = queryParams.toString()

	const response = await axios.get(`/booking/get-user-current-bookings?${queryString}`)
	// The endpoint answers 200 with a not-found body when the user has none, so the
	// callers can tell "empty" apart from "still loading".
	return response.data?.response?.data ?? []
}

const fetchReferralUsersData = async (
	data: FilterPayload,
): Promise<{ data: Referral[]; pagination: Pagination }> => {
	const queryParams = new URLSearchParams()
	queryParams.append('page', data.pageNumber)
	queryParams.append('limit', data.pageSize)

	if (data.filterColumn && data.filterValue) {
		queryParams.append('filterColumn', data.filterColumn)
		queryParams.append('filterValue', data.filterValue)
	}

	const queryString = queryParams.toString()

	const response = await axios.get(`/user/get-referral-users?${queryString}`)
	return response.data
}

const togglePassword = async ({ id, action, ...rest }: TogglePasswordPayload) => {
	const body: Record<string, any> = { action }
	if (action === 'update' && 'newPassword' in rest) {
		body.newPassword = rest.newPassword
	}

	const response = await axios.put(`/user/password-toggle/${id}`, body)
	return response.data
}

const fetchReferralCount = async (): Promise<number> => {
	const response = await axios.get('/user/get-my-referral-users')
	return response.data.length
}

const generateReferralCode = async () => {
	const response = await axios.put('/user/generateReferralCode')
	return response.data
}

const updateSpecificUserProfile = async (id: User['id'], data: UserUpdateForm) => {
	const response = await axios.put(`/user/${id}`, data)
	return response.data
}

const updateUserStatus = async (id: User['id'], status: 0 | 1) => {
	const response = await axios.put(`/user/status/${id}`, { status })
	return response.data
}

const updateUserProfile = async (data: UserForm) => {
	const response = await axios.put('/user', data)
	return response.data
}

const userService = {
	fetchAllUsers,
	fetchReferralUsersData,
	fetchUserBookings,
	fetchUserCurrentBookings,
	fetchReferralCount,
	generateReferralCode,
	fetchUserActiveBookings,
	fetchUserEarnings,
	fetchUsersData,
	updateSpecificUserProfile,
	updateUserStatus,
	updateUserProfile,
	togglePassword,
}

export default userService
